import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CityService from '../../../services/city.service';
import SalePointTypeService from '../../../services/salePointType.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column', 
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, initialData = null, onSubmit, onCancel }) {
  const [formData, setFormData] = React.useState({
    name: '',
    address: '',
    opening_period: '',
    city_id: '',
    sales_point_type_id: ''
  });

  const [cities, setCities] = React.useState([]);
  const [salePointTypes, setSalePointTypes] = React.useState([]);

  React.useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [citiesData, typesData] = await Promise.all([
          CityService.list(),
          SalePointTypeService.list()
        ]);
        setCities(citiesData);
        setSalePointTypes(typesData);
      } catch (error) {
        console.error('Erreur lors du chargement des données de référence:', error);
      }
    };
    loadReferenceData();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        address: initialData.address || '',
        opening_period: initialData.opening_period || '',
        city_id: initialData.city.id || '',
        sales_point_type_id: initialData.sale_point_type.id || ''
      });
    } else {
      setFormData({
        name: '',
        address: '',
        opening_period: '',
        city_id: '',
        sales_point_type_id: ''
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const submittedData = {
      ...formData,
      id: initialData?.id
    };
    onSubmit(submittedData);
  };

  const handleCancel = () => {
    onCancel();
    setFormData({
      name: '',
      address: '',
      opening_period: '',
      city_id: '',
      sales_point_type_id: ''
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier le point de vente' : 'Nouveau point de vente'}</span>
        <IconButton
          onClick={handleCancel}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Adresse"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            multiline
            rows={2}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Période d'ouverture"
            name="opening_period"
            value={formData.opening_period}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            select
            fullWidth
            label="Ville"
            name="city_id"
            value={formData.city_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Type de point de vente"
            name="sales_point_type_id"
            value={formData.sales_point_type_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {salePointTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button
            onClick={handleCancel}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    opening_period: PropTypes.string,
    city_id: PropTypes.number,
    sales_point_type_id: PropTypes.number
  })
};

export default Form;